import Vue from 'vue';
import { getAnalysesFromIds } from '@/shared/store/analysisHelper';
import { types as authentication } from '@/shared/store/authentication';
import { types as reflectionsTypes } from './reflections';
import { getById, findRaw, addElementToArray, update } from '../firebase/firestoreFunctions';
import { configureQueryByCollectionPath, queryIterator } from '../shared/firebase/firestore';
import { FirestoreOrderBy, FirestoreQuery } from '../firebase/firestoreQuery';
import i18n from '../i18n';

const MODULE_PREFIX = 'recommendationBasedJournals';
const GETTER_JOURNAL = 'recommendationBasedJournal';
const GETTER_ORG_JOURNAL = 'orgJournal';
const GETTER_SPOTLIGHT_JOURNAL = 'spotlightJournal';
const GETTER_JOURNAL_HISTORY = 'journalHistory';
const GETTER_USER_PIPELINED_JOURNAL = 'userStandaloneJournals';
const GETTER_GROUP_PIPELINED_JOURNAL = 'groupStandaloneJournals';
const GETTER_REFLECTION_PIPELINED_JOURNAL = 'userReflectionJournals';
const GETTER_NOOS_DOCUMENTS = 'noosDocuments';
const MUTATION_RECOMMENDATION_RATED = 'MUTATION_RECOMMENDATION_RATED';
const MUTATION_FETCHED_JOURNAL = 'FETCHED_RECOMMENDATION_BASED_JOURNAL';
const MUTATION_FETCHED_JOURNAL_HISTORY = 'MUTATION_FETCHED_JOURNAL_HISTORY';
const MUTATION_FETCHED_SPOTLIGHT_JOURNAL = 'FETCHED_SPOTLIGHT_JOURNAL';
const MUTATION_FETCHED_PIPELINED_JOURNAL_FOR_USER = 'FETCHED_PIPELINED_JOURNAL_FOR_USER';
const MUTATION_FETCHED_PIPELINED_JOURNAL_FOR_GROUPS = 'FETCHED_PIPELINED_JOURNAL_FOR_GROUPS';
const MUTATION_FETCHED_PIPELINED_JOURNAL_FOR_REFLECTIONS = 'FETCHED_PIPELINED_JOURNAL_FOR_REFLECTIONS';
const MUTATION_FETCHED_ORG_JOURNAL = 'FETCHED_ORG_JOURNAL';
const MUTATION_FETCHED_DOCUMENT_CONTENT_URL = 'FETCHED_DOCUMENT_CONTENT_URL';
const MUTATION_FETCHED_DOCUMENT_LEAD_IMAGE_URL = 'FETCHED_DOCUMENT_LEAD_IMAGE_URL';
const MUTATION_ARCHIVE_PIPELINED_JOURNAL = 'MUTATION_ARCHIVE_PIPELINED_JOURNAL';
const ACTION_FETCH_ORG_JOURNAL = 'fetchOrgJournal';
const ACTION_FETCH_SPOTLIGHT_JOURNAL = 'fetchSpotlightJournal';
const ACTION_FETCH_PIPELINED_JOURNAL = 'fetchPipelinedJournal';
const ACTION_FETCH_JOURNAL_HISTORY = 'fetchJournalHistory';
const ACTION_ARCHIVE_PIPELINED_JOURNAL = 'archivePipelinedJournal';
const ACTION_RECOMMENDATION_CLICKED = 'recommendationClicked';
const ACTION_RECOMMENDATION_CLICKED_SOURCE = 'recommendationClickedSource';
const ACTION_RECOMMENDATION_RATED = 'recommendationRated';
const ACTION_RECOMMENDATION_DISCARDED = 'recommendationDiscarded';
const ACTION_RECOMMENDATION_UNDISCARTED = 'recommendationUnDiscarted';
const ACTION_RECOMMENDATION_MOVED_TO_PORTFOLIO = 'recommendationMovedToPortfolio';
const ACTION_RECOMMENDATION_SHARED = 'recommendationShared';
const ACTION_FETCH_VALIDATION_JOURNAL = 'fetchValidationJournal';

const MAX_FETCHED_RECOMMENDATION_PER_SECTION = 32; // Upper limit
const RecommendationEvent = {
  New: 'new',
  Rated: 'rated',
  Clicked: 'clicked',
  ClickedSource: 'clickedSource',
  Outdated: 'outdated',
  Viewed: 'viewed',
  Pinned: 'pinned',
  Discarded: 'discarded',
  UnDiscarded: 'undiscarded',
  ToPortfolio: 'toPortfolio',
  Shared: 'shared',
};

const registerRecommendationInteractionEvent = async (recommendation, event, userId, details = undefined) => {
  const eventTrigger = {
    type: 'user',
    id: userId,
  };
  if (!recommendation) {
    // Not from a recommendation.
    return;
  }
  if (details) {
    eventTrigger.details = details;
  }
  await addElementToArray('pipelineJournalsRecommendations', recommendation.id, 'events', {
    type: event,
    timestamp: Date.now(),
    eventTrigger,
  });
};


const initialState = {
  journal: {
    portfolio: [],
    groups: {},
  },
  journalHistory: {},
  userStandaloneJournals: [],
  groupStandaloneJournals: [],
  userReflectionJournals: [],
  orgJournal: [],
  spotlightJournal: {},
  noosDocuments: [],
};

const getters = {
  [GETTER_JOURNAL]: state => state.journal,
  [GETTER_ORG_JOURNAL]: state => state.orgJournal,
  [GETTER_USER_PIPELINED_JOURNAL]: state => state.userStandaloneJournals,
  [GETTER_GROUP_PIPELINED_JOURNAL]: state => state.groupStandaloneJournals,
  [GETTER_REFLECTION_PIPELINED_JOURNAL]: state => state.userReflectionJournals,
  [GETTER_NOOS_DOCUMENTS]: state => state.noosDocuments,
  [GETTER_SPOTLIGHT_JOURNAL]: state => state.spotlightJournal,
  [GETTER_JOURNAL_HISTORY]: state => state.journalHistory,
};

const mutations = {
  [MUTATION_FETCHED_JOURNAL]: (state, journal) => {
    state.journal = journal;
  },
  [MUTATION_FETCHED_JOURNAL_HISTORY]: (state, { documents, iterator, journalId }) => {
    state.journalHistory[journalId] = {
      documents,
      journalId,
      iterator,
    };
  },
  [MUTATION_FETCHED_SPOTLIGHT_JOURNAL]: (state, { spotlightJournal, userId }) => {
    state.spotlightJournal = spotlightJournal;
    spotlightJournalList(state).forEach(journal => {
      setRecommendationUserRating(journal.recommendations, userId);
    });
  },
  [MUTATION_ARCHIVE_PIPELINED_JOURNAL]: (state, journalId) => {
    Vue.set(state, 'userStandaloneJournals', state.userStandaloneJournals.filter(u => u.journal.id !== journalId));
    Vue.set(state, 'userReflectionJournals', state.userReflectionJournals.filter(u => u.journal.id !== journalId));
  },
  [MUTATION_RECOMMENDATION_RATED]: (state, { recommendationId, rating }) => {
    const journals = [
      ...spotlightJournalList(state),
      ...state.userStandaloneJournals,
      ...state.groupStandaloneJournals,
      ...state.userReflectionJournals,
    ];
    for (let i = 0; i < journals.length; i++) {
      for (let j = 0; j < journals[i].recommendations.length; j++) {
        if (journals[i].recommendations[j].recommendation.id === recommendationId) {
          Vue.set(journals[i].recommendations[j].recommendation, 'rating', rating);
          return;
        }
      }
    }
  },
  [MUTATION_FETCHED_ORG_JOURNAL]: (state, journal) => {
    state.orgJournal = journal;
  },
  [MUTATION_FETCHED_DOCUMENT_CONTENT_URL]: (state, { documentId, url }) => {
    const matchingDocument = state.noosDocuments.find(candidateDocument => candidateDocument.id === documentId);

    if (matchingDocument) {
      matchingDocument.contentUrl = url;
    }
  },
  [MUTATION_FETCHED_DOCUMENT_LEAD_IMAGE_URL]: (state, { documentId, url }) => {
    const matchingDocument = state.noosDocuments.find(candidateDocument => candidateDocument.id === documentId);

    if (matchingDocument) {
      matchingDocument.leadImageUrl = url;
    }
  },
  [MUTATION_FETCHED_PIPELINED_JOURNAL_FOR_USER]: (state, { pipelinedJournal, userId }) => {
    state.userStandaloneJournals = pipelinedJournal;
    pipelinedJournal.forEach(journal => {
      setRecommendationUserRating(journal.recommendations, userId);
    });
  },
  [MUTATION_FETCHED_PIPELINED_JOURNAL_FOR_GROUPS]: (state, { pipelinedJournal, userId }) => {
    state.groupStandaloneJournals = pipelinedJournal;
    pipelinedJournal.forEach(journal => {
      setRecommendationUserRating(journal.recommendations, userId);
    });
  },
  [MUTATION_FETCHED_PIPELINED_JOURNAL_FOR_REFLECTIONS]: (state, { pipelinedJournal, userId }) => {
    state.userReflectionJournals = pipelinedJournal;
    pipelinedJournal.forEach(journal => {
      setRecommendationUserRating(journal.recommendations, userId);
    });
  },

};

const spotlightJournalList = (state) => {
  const journals = [];

  const addSpotlightJournal = (journal) => {
    if (journal) {
      journals.push(journal);
    }
  };

  addSpotlightJournal(state.spotlightJournal.groupsAnalyses);
  addSpotlightJournal(state.spotlightJournal.similarToGroupsAnalyses);
  addSpotlightJournal(state.spotlightJournal.similarToUserAnalyses);
  addSpotlightJournal(state.spotlightJournal.organization);

  return journals;
};

const setRecommendationUserRating = (recommendations, userId) => {
  recommendations.forEach(recommendation => {
    const latestUserRating = findLatestUserRating(recommendation.recommendation.events, userId);

    if (latestUserRating) {
      recommendation.recommendation.rating = latestUserRating;
    }
  });
};

const findLatestUserRating = (recommendationEvents, userId) => {
  if (!recommendationEvents) {
    return undefined;
  }

  return recommendationEvents.reduce((rating, event) => {
    if (event.type === 'rated' && event.eventTrigger.type === 'user' && event.eventTrigger.id === userId) {
      return event.eventTrigger.details.rating;
    }
    return rating;
  }, undefined);
};

const fetchOrgJournal = async (orgId) => {
  const orgJournalSnapshot = await findRaw(`organizations/${orgId}/journal`, [], { field: 'timestamp', order: 'desc' }, 9);
  return orgJournalSnapshot.docs.map(doc => doc.data());
};

const fetchDocumentDetails = async (recommendation, userId) => {
  if (recommendation.analysisId) {
    try {
      const analyses = await getAnalysesFromIds(userId, '', [recommendation.analysisId]);
      if (analyses.length === 0) return undefined;

      const analysis = analyses[0];
      if (!analysis) return undefined;

      const { documentDetails, acl } = analysis;
      documentDetails.analysisId = recommendation.analysisId;
      documentDetails.documentType = 'document';
      documentDetails.acl = acl;
      return documentDetails;
    } catch (error) {
      console.error('Cannot fetch analysis', recommendation.analysisId, error);
      return undefined;
    }
  } else {
    try {
      const documentDetails = await getById('noosDocuments', recommendation.documentId);
      if (!documentDetails) return undefined;

      documentDetails.documentType = 'document';
      return documentDetails;
    } catch (error) {
      console.error('Cannot fetch public document', recommendation.documentId, error);
      return undefined;
    }
  }
};

const formatRecommendation = async (recommendation, userId) => {
  const documentDetails = await fetchDocumentDetails(recommendation, userId);
  if (!documentDetails) return undefined;

  recommendation.documentData = documentDetails;
  return recommendation;
};

const fetchPipelineRecommendedDocuments = async (journalDefinitions, userId) => {
  if (!journalDefinitions) return [];

  const defsWithRecommendations = await Promise.all(journalDefinitions.map(async (def) => ({
    ...def,
    recommendations: await findRaw('pipelineJournalsRecommendations',
      [
        FirestoreQuery.equals('journalId', def.journal.id),
        FirestoreQuery.equals('exhausted', false),
      ],
      // TODO created indexes and switch the orderBy
      // FirestoreOrderBy.descending('recommendation.score'),
      FirestoreOrderBy.descending('strategyInformation.recommendationTimestamp'),
      MAX_FETCHED_RECOMMENDATION_PER_SECTION),
  })));

  const sections = await Promise.all(defsWithRecommendations.map(async def => {
    const recommendationsData = await Promise.all(def.recommendations.docs
      .map(rec => ({ ...rec.data(), id: rec.id }))
      .map(rec => ({
        recommendation: rec,
        documentId: rec.recommendation.documentId,
        analysisId: rec.recommendation.analysisId,
        score: rec.recommendation.score,
      }))
      .map(rec => formatRecommendation(rec, userId)));

    const recommendations = recommendationsData.filter(rec => rec !== undefined).sort((a, b) => b.score - a.score);

    return {
      ...def,
      recommendations,
    };
  }));
  return sections;
};

const fetchJournalSection = async (journal, title, userId) => {
  if (!journal) return undefined;

  const payload = [{ journal: { ...journal, title } }];
  const sections = await fetchPipelineRecommendedDocuments(payload, userId);

  return sections[0];
};


const fetchSpotlightJournal = async (userId, organizationId) => {
  const organizationJournal = await findSpotlightOrganizationJournal(organizationId);

  // If no organization group exist, the spotlight journal is not fetched and not displayed.
  if (!organizationJournal) {
    return {
      groupsAnalyses: undefined,
      similarToGroupsAnalyses: undefined,
      similarToUserAnalyses: undefined,
      organization: undefined,
    };
  }

  const similarToGroupsAnalysesJournal = await findSpotlightJournal(userId, 'SIMILAR_TO_GROUPS_ANALYSES');
  const similarToUserAnalysesJournal = await findSpotlightJournal(userId, 'SIMILAR_TO_USER_ANALYSES');
  const groupsPortfolioJournal = await findSpotlightJournal(userId, 'GROUPS_ANALYSES');

  const sections = await Promise.all(
    [
      fetchJournalSection(groupsPortfolioJournal, i18n.t('views.journal.sections.groupsAnalyses'), userId),
      fetchJournalSection(similarToGroupsAnalysesJournal, i18n.t('views.journal.sections.relatedToGroupsAnalyses'), userId),
      fetchJournalSection(similarToUserAnalysesJournal, i18n.t('views.journal.sections.relatedToUserAnalyses'), userId),
      fetchJournalSection(organizationJournal, i18n.t('views.journal.sections.organisation'), userId),
    ],
    userId,
  );

  return {
    groupsAnalyses: sections[0],
    similarToGroupsAnalyses: sections[1],
    similarToUserAnalyses: sections[2],
    organization: sections[3],
  };
};

const findSpotlightJournal = async (userId, template) => {
  const snapshots = await findRaw('pipelineJournals', [
    { key: 'type', operator: '==', value: 'spotlight' },
    { key: 'entityType', operator: '==', value: 'user' },
    { key: 'entityId', operator: '==', value: userId },
    { key: 'template', operator: '==', value: template },
  ]);

  if (snapshots.docs.length > 0) {
    const snapshot = snapshots.docs[0];
    return snapshot.data();
  }

  console.warn(`Unable to find spotlight journal for user ${userId} and template ${template}`);
  return undefined;
};

const findSpotlightOrganizationGroupId = async (organizationId) => {
  const organization = await getById('organizations', organizationId);
  return organization.organizationGroup;
};

const findSpotlightOrganizationJournal = async (organizationId) => {
  const groupId = await findSpotlightOrganizationGroupId(organizationId);
  if (!groupId) return undefined;

  const snaphsots = await findRaw('pipelineJournals', [
    FirestoreQuery.equals('entityType', 'group'),
    FirestoreQuery.equals('entityId', groupId),
    FirestoreQuery.equals('status', 'active'),
    FirestoreQuery.equals('type', 'spotlight'),
  ]);

  if (snaphsots.docs.length > 0) {
    return snaphsots.docs[0].data();
  }

  return undefined;
};

const fetchAllPipelinedJournals = async (userId, groups, reflections) => {
  const pipelineJournals = {
    user: [],
    group: [],
    reflection: [],
  };
  const processSnapshot = (snapshot) => {
    const journalData = snapshot.data();
    return {
      journal: {
        title: journalData.name,
        ...journalData,
      },
    };
  };

  const fetchUserJournals = async () => {
    const userJournal = await findRaw('pipelineJournals',
      [
        FirestoreQuery.equals('entityType', 'user'),
        FirestoreQuery.equals('entityId', userId),
        FirestoreQuery.equals('status', 'active'),
        FirestoreQuery.equals('type', 'standalone'),
      ]);
    if (userJournal.empty) {
      return;
    }
    userJournal.docs.forEach(snapshot => {
      const journalData = snapshot.data();
      if (journalData.name === 'Portfolio') {
        return;
      }
      pipelineJournals.user.push(processSnapshot(snapshot));
    });
  };

  const fetchGroupJournals = async () => {
    await Promise.all(groups.map(async group => {
      const groupJournal = await findRaw('pipelineJournals',
        [
          FirestoreQuery.equals('entityType', 'group'),
          FirestoreQuery.equals('entityId', group.id),
          FirestoreQuery.equals('status', 'active'),
          FirestoreQuery.equals('type', 'standalone'),
        ]);

      if (groupJournal.empty) {
        return;
      }
      groupJournal.docs.forEach(snapshot => {
        pipelineJournals.group.push(processSnapshot(snapshot));
      });
    }));
  };
  const fetchReflectionJournals = async () => {
    await Promise.all(reflections.map(async reflection => {
      const reflectionJournal = await findRaw('pipelineJournals',
        [
          FirestoreQuery.equals('entityType', 'reflection'),
          FirestoreQuery.equals('entityId', reflection.id),
          FirestoreQuery.equals('status', 'active'),
          FirestoreQuery.equals('type', 'standalone'),
        ]);

      if (reflectionJournal.empty) {
        return;
      }
      reflectionJournal.docs.forEach(snapshot => {
        pipelineJournals.reflection.push(processSnapshot(snapshot));
      });
    }));
  };

  await Promise.all([fetchUserJournals(), fetchGroupJournals(), fetchReflectionJournals()]);
  sortPipelinedJournals(pipelineJournals);
  return pipelineJournals;
};

const sortPipelinedJournals = (pipelineJournals) => {
  const sortByName = (a, b) => a.journal.name.localeCompare(b.journal.name);

  pipelineJournals.user.sort((a, b) => sortByName(a, b));
  pipelineJournals.group.sort((a, b) => sortByName(a, b));
  pipelineJournals.reflection.sort((a, b) => sortByName(a, b));
};

const fetchPipelinedJournal = async (rootGetters) => {
  const userId = rootGetters[authentication.getters.userId];
  const groups = rootGetters[authentication.getters.groups];
  const reflections = rootGetters[reflectionsTypes.getters.reflections];

  const pipelineJournals = {
    group: [],
    user: [],
    reflection: [],
  };

  try {
    const journalDefinitions = await fetchAllPipelinedJournals(userId, groups, reflections);

    // TODO load template and limit accordingly
    pipelineJournals.group = await fetchPipelineRecommendedDocuments(journalDefinitions.group, userId);
    pipelineJournals.user = await fetchPipelineRecommendedDocuments(journalDefinitions.user, userId);
    pipelineJournals.reflection = await fetchPipelineRecommendedDocuments(journalDefinitions.reflection, userId);

    return pipelineJournals;
  } catch (e) {
    console.error(e);
    throw Error(`Failed to fetch pipelineJournals ${e}`);
  }
};

const updateJournalHistory = async (journalHistory, userId) => {
  const nextRecommendations = await journalHistory.iterator.next();
  if (nextRecommendations.done) return journalHistory;

  const recommendations = nextRecommendations.value;
  const documentsRaw = await Promise.all(
    recommendations.map(recommendation => formatRecommendation(recommendation.recommendation, userId)),
  );
  const newDocuments = documentsRaw.map(doc => ({
    ...doc,
    documentDetails: doc.documentData,
  }));

  return {
    iterator: journalHistory.iterator,
    journalId: journalHistory.journalId,
    documents: journalHistory.documents.concat(newDocuments),
  };
};

const getCurrentJournalHistory = (rootGetters, journalId) => {
  const journalHistory = rootGetters[types.getters.journalHistory];
  const currentJournalHistory = journalHistory[journalId];
  if (currentJournalHistory) return currentJournalHistory;

  const queryFetching = () => configureQueryByCollectionPath('pipelineJournalsRecommendations',
    [
      FirestoreQuery.equals('journalId', journalId),
      FirestoreQuery.equals('exhausted', true),
    ],
    FirestoreOrderBy.descending('deactivationTimestamp'));


  return {
    documents: [],
    journalId,
    iterator: queryIterator(queryFetching, 20),
  };
};

const actions = {
  [ACTION_FETCH_PIPELINED_JOURNAL]: async ({ commit, rootGetters }) => {
    const pipelinedJournalPromise = fetchPipelinedJournal(rootGetters);
    const pipelinedJournal = await pipelinedJournalPromise;
    const userId = rootGetters[authentication.getters.userId];

    commit(MUTATION_FETCHED_PIPELINED_JOURNAL_FOR_USER, { pipelinedJournal: pipelinedJournal.user, userId });
    commit(MUTATION_FETCHED_PIPELINED_JOURNAL_FOR_GROUPS, { pipelinedJournal: pipelinedJournal.group, userId });
    commit(MUTATION_FETCHED_PIPELINED_JOURNAL_FOR_REFLECTIONS, {
      pipelinedJournal: pipelinedJournal.reflection,
      userId,
    });
  },
  [ACTION_FETCH_JOURNAL_HISTORY]: async ({ commit, rootGetters }, journalId) => {
    const userId = rootGetters[authentication.getters.userId];
    const currentJournalHistory = getCurrentJournalHistory(rootGetters, journalId);
    const updatedJournalHistory = await updateJournalHistory(currentJournalHistory, userId);

    if (currentJournalHistory.documents.length === updatedJournalHistory.documents.length) return;

    commit(MUTATION_FETCHED_JOURNAL_HISTORY, updatedJournalHistory);
  },
  [ACTION_ARCHIVE_PIPELINED_JOURNAL]: async ({ commit }, { pipelinedJournalId }) => {
    await update('pipelineJournals', pipelinedJournalId, { status: 'archived' });

    commit(MUTATION_ARCHIVE_PIPELINED_JOURNAL, pipelinedJournalId);
  },
  [ACTION_FETCH_SPOTLIGHT_JOURNAL]: async ({ commit, rootGetters }) => {
    const userId = rootGetters[authentication.getters.userId];
    const organizationId = rootGetters[authentication.getters.organizationId];

    const spotlightJournal = await fetchSpotlightJournal(userId, organizationId);

    commit(MUTATION_FETCHED_SPOTLIGHT_JOURNAL, { spotlightJournal, userId });
  },
  [ACTION_FETCH_VALIDATION_JOURNAL]: async ({ rootGetters }) => {
    const organizations = [
      {
        id: '291d805f-e2ae-42da-989f-d0f3edec5898',
        name: 'SDC Montcalm',
      },
      {
        id: '72084243-f827-45aa-8e26-20e62b7b4d90',
        name: 'Quebec international',
      },
      {
        id: 'UFB4NkoQ5loxKoYzdVeG',
        name: 'Agence Middle',
      },
      {
        id: 'W35grGXC5ZBTb9KNzSia',
        name: 'Serge Beauchemin',
      },
      {
        id: 'f8deebe1-8e0c-41d4-905a-c6074a5aabbe',
        name: 'Protegez-vous',
      },
      {
        id: 'f8ioKWbbK0E3r9aqxaaG',
        name: 'Cyber Swat',
      },
      {
        id: 'kuH6osFGjhdJiD7aNXRY',
        name: 'Credo impact',
      },
      {
        id: 'nwJEngEv8cK5HfhQGrCz',
        name: 'MRC des Chenaux',
      },
      {
        id: 's5wn1pAIlu1A49v3bPMM',
        name: 'IA7',
      },
      {
        id: '5uBQ46unO62SFGpVXpth',
        name: 'Fonds d\'emprunt',
      },
    ];
    const userId = rootGetters[authentication.getters.userId];

    return Promise.all(organizations.map(async org => (
      {
        orgId: org.id,
        orgName: org.name,
        orgJournal: await fetchOrgJournal(org.id),
        spotlightJournal: await fetchJournalSection(await findSpotlightOrganizationJournal(org.id), userId),
      }
    )));
  },
  [ACTION_FETCH_ORG_JOURNAL]: async ({ commit, rootGetters }) => {
    const orgId = rootGetters[authentication.getters.organizationId];
    const orgJournalPromise = fetchOrgJournal(orgId);
    commit(MUTATION_FETCHED_ORG_JOURNAL, await orgJournalPromise);
  },
  [ACTION_RECOMMENDATION_MOVED_TO_PORTFOLIO]: async ({ rootGetters }, { doc }) => {
    const userId = rootGetters[authentication.getters.userId];
    await registerRecommendationInteractionEvent(
      doc.documentDetails.recommendation,
      RecommendationEvent.ToPortfolio,
      userId,
    );
  },
  [ACTION_RECOMMENDATION_SHARED]: async ({ rootGetters }, { doc, groupId }) => {
    const userId = rootGetters[authentication.getters.userId];
    await registerRecommendationInteractionEvent(
      doc.documentDetails.recommendation,
      RecommendationEvent.Shared,
      userId,
      { groupId },
    );
  },
  [ACTION_RECOMMENDATION_DISCARDED]: async ({ rootGetters, commit }, { recommendation }) => {
    const userId = rootGetters[authentication.getters.userId];
    await update('pipelineJournalsRecommendations', recommendation.id, { rating: 0 });
    await registerRecommendationInteractionEvent(
      recommendation,
      RecommendationEvent.Discarded,
      userId,
    );
    commit(MUTATION_RECOMMENDATION_RATED, { recommendationId: recommendation.id, rating: 0 });
  },
  [ACTION_RECOMMENDATION_UNDISCARTED]: async ({ rootGetters }, { recommendation }) => {
    const userId = rootGetters[authentication.getters.userId];
    await registerRecommendationInteractionEvent(
      recommendation,
      RecommendationEvent.UnDiscarded,
      userId,
    );
  },
  [ACTION_RECOMMENDATION_RATED]: async ({ rootGetters, commit }, { recommendation, rating }) => {
    const userId = rootGetters[authentication.getters.userId];
    commit(MUTATION_RECOMMENDATION_RATED, { recommendationId: recommendation.id, rating });
    await registerRecommendationInteractionEvent(
      recommendation,
      RecommendationEvent.Rated,
      userId,
      { rating },
    );
  },
  [ACTION_RECOMMENDATION_CLICKED_SOURCE]: async ({ rootGetters }, { doc }) => {
    const userId = rootGetters[authentication.getters.userId];
    await registerRecommendationInteractionEvent(
      doc.documentDetails.recommendation,
      RecommendationEvent.ClickedSource,
      userId,
    );
  },
  [ACTION_RECOMMENDATION_CLICKED]: async ({ rootGetters }, { doc }) => {
    const delay = 10000;
    const userId = rootGetters[authentication.getters.userId];
    await registerRecommendationInteractionEvent(
      doc.documentDetails.recommendation,
      RecommendationEvent.Clicked,
      userId,
    );
    setTimeout(() => {
      // TODO read from router or something more reliable
      if (window.location.href.includes(`/doc/${doc.documentId}`)) {
        registerRecommendationInteractionEvent(
          doc.documentDetails.recommendation,
          RecommendationEvent.Viewed,
          userId,
          { readTime: delay },
        );
      }
    }, delay);
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions,
};

export const types = {
  getters: {
    recommendationBasedJournal: `${MODULE_PREFIX}/${GETTER_JOURNAL}`,
    orgJournal: `${MODULE_PREFIX}/${GETTER_ORG_JOURNAL}`,
    userStandaloneJournals: `${MODULE_PREFIX}/${GETTER_USER_PIPELINED_JOURNAL}`,
    groupStandaloneJournals: `${MODULE_PREFIX}/${GETTER_GROUP_PIPELINED_JOURNAL}`,
    userReflectionJournals: `${MODULE_PREFIX}/${GETTER_REFLECTION_PIPELINED_JOURNAL}`,
    spotlightJournal: `${MODULE_PREFIX}/${GETTER_SPOTLIGHT_JOURNAL}`,
    journalHistory: `${MODULE_PREFIX}/${GETTER_JOURNAL_HISTORY}`,
  },
  actions: {
    fetchOrgJournal: `${MODULE_PREFIX}/${ACTION_FETCH_ORG_JOURNAL}`,
    fetchValidationJournal: `${MODULE_PREFIX}/${ACTION_FETCH_VALIDATION_JOURNAL}`,
    fetchJournalHistory: `${MODULE_PREFIX}/${ACTION_FETCH_JOURNAL_HISTORY}`,
    fetchSpotlightJournal: `${MODULE_PREFIX}/${ACTION_FETCH_SPOTLIGHT_JOURNAL}`,
    fetchPipelinedJournal: `${MODULE_PREFIX}/${ACTION_FETCH_PIPELINED_JOURNAL}`,
    archivePipelinedJournal: `${MODULE_PREFIX}/${ACTION_ARCHIVE_PIPELINED_JOURNAL}`,
    recommendationClicked: `${MODULE_PREFIX}/${ACTION_RECOMMENDATION_CLICKED}`,
    recommendationClickedSource: `${MODULE_PREFIX}/${ACTION_RECOMMENDATION_CLICKED_SOURCE}`,
    recommendationDiscarded: `${MODULE_PREFIX}/${ACTION_RECOMMENDATION_DISCARDED}`,
    recommendationUnDiscarted: `${MODULE_PREFIX}/${ACTION_RECOMMENDATION_UNDISCARTED}`,
    recommendationRated: `${MODULE_PREFIX}/${ACTION_RECOMMENDATION_RATED}`,
    recommendationShared: `${MODULE_PREFIX}/${ACTION_RECOMMENDATION_SHARED}`,
    recommendationMovedToPortfolio: `${MODULE_PREFIX}/${ACTION_RECOMMENDATION_MOVED_TO_PORTFOLIO}`,
  },
};
